import { DownloadIcon } from '@phosphor-icons/react';
import * as Sentry from '@sentry/react';
import {
  Cascade,
  CascadeFileNameEnum,
  Invoice,
  InvoiceFileNameEnum,
  InvoiceTypeEnum,
  ServiceAccessEnum,
} from '@sr-sdks/api-sdk-axios';
import { Badge, Button, DropdownMenuItem, Icon } from '@sr/ui-library';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchInvoices, useInvoices } from '../../../../api/payments';
import {
  CASCADES_DEFAULT_PARAMS,
  useCascades,
} from '../../../../api/services/cascadesApi';
import AppTableV2 from '../../../../components/AppTableV2';
import CurrencyFormatter from '../../../../components/CurrencyFormatter';
import DateFormatter from '../../../../components/DateFormatter';
import { IfFeatureEnabled } from '../../../../components/IfFeatureEnabled';
import IfHasServiceAccess from '../../../../components/IfHasServiceAccess';
import OptionsDropdown from '../../../../components/OptionsDropdown';
import { AppDataTable } from '../../../../components/data-table';
import { LAST_SUCCESSFUL_DATA_SCOPES } from '../../../../constant/lastSuccessfulDataScopes';
import { useLastSuccessfulData } from '../../../../hooks/useLastSuccessfulData';
import { pdfIcon } from '../../../../lib/srIcons';
import { reduxServices } from '../../../../reducers/reduxServices';
import { beneficiaryService } from '../../../../reducers/services/beneficiarySlice';
import { useTypedSelector } from '../../../../stateStore';
import downloadApi from '../../../../utils/api/downloadApi';
import { toTitleCase } from '../../../../utils/helpers/common';

type LoadingInvoiceType = { invoiceId: string; isLoading: boolean };
type LoadingStatementType = { isLoading: boolean; statementId: string };

type CascadeRowData = Cascade;

// Stable reference so an unresolved `cascadeData` never recreates the array
// identity on every render — it feeds the fee-invoice effect's deps below.
const NO_CASCADES: Cascade[] = [];

/**
 * Activity Statements table using the new AppDataTable component.
 * Reimplements ActivityStatementsTable with AppDataTable, preserving exact
 * behavior: data fetching via TanStack Query (see `useCascades`), same
 * columns, same row actions (downloads with per-row loading states), and
 * expandable invoice nested table.
 */
export const ActivityStatementsDataTable = () => {
  const currentBeneficiary = useTypedSelector(
    beneficiaryService.selectors.currentBeneficiary,
  );

  const [downloadInvoiceProps, setDownloadInvoiceProps] =
    useState<LoadingInvoiceType>();
  const [downloadStatementProps, setDownloadStatementProps] =
    useState<LoadingStatementType>();

  const [selectedCascadeId, setSelectedCascadeId] = useState<null | string>(
    null,
  );

  const [feeInvoiceAvailability, setFeeInvoiceAvailability] = useState<
    Record<string, boolean>
  >({});
  // Ids with a fee-invoice check already in flight — guards fast Next/Prev
  // paging from firing a duplicate `fetchInvoices` for the same cascade.
  const feeInvoiceInFlightIds = useRef<Set<string>>(new Set());

  // Seeded from the URL on first render (not just `useState(1)`) so a deep
  // link like `?cascadesTablePage=3` fetches page 3 straight away instead of
  // requesting page 1 first and correcting itself once DataTablePagination's
  // mount effect reports the real URL page.
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(
    () =>
      Number(searchParams.get('cascadesTablePage')) ||
      CASCADES_DEFAULT_PARAMS.page,
  );
  const [sortBy, setSortBy] = useState(
    () =>
      searchParams.get('cascadesTableSortBy') ?? CASCADES_DEFAULT_PARAMS.sortBy,
  );

  const queryParams = useMemo(() => {
    if (!currentBeneficiary) return undefined;

    return {
      beneficiaryTitleId: currentBeneficiary.beneficiaryTitleId,
      page,
      pageSize: 50,
      serviceId: currentBeneficiary.serviceId,
      sortBy,
    };
  }, [currentBeneficiary, page, sortBy]);

  const {
    data: cascadeData,
    isError: isCascadesError,
    isLoading: isCascadesLoading,
    isPlaceholderData,
  } = useCascades(queryParams, {
    placeholderData: keepPreviousData,
  });

  // A failed page>1 fetch settles `cascadeData` to `undefined` —
  // `keepPreviousData` only covers the pending state, not error. Falling
  // back to the last successful response keeps Prev/Next usable instead of
  // stranding the user on an empty table.
  const lastGoodCascadeData = useLastSuccessfulData(
    cascadeData,
    isCascadesError,
    LAST_SUCCESSFUL_DATA_SCOPES.ACTIVITY_STATEMENTS,
    currentBeneficiary,
  );

  const cascades = lastGoodCascadeData?.items ?? NO_CASCADES;
  const paginationMeta = lastGoodCascadeData?.meta;

  const featuresEnabled = useTypedSelector(
    reduxServices.setting.selectors.features,
  );
  const isDownloadIndividualBeneficiaryEnabled =
    featuresEnabled?.[
      'workspace.titles.collectionAccountManagement.downloadIndividualBeneficiary'
    ] === 'true';

  const selectedCascade =
    cascades?.find((cascade) => cascade._id === selectedCascadeId) ?? null;

  const { data: invoiceData, isLoading: isInvoiceLoading } = useInvoices(
    {
      cascadeId: selectedCascade?._id,
      invoiceType: InvoiceTypeEnum.Send,
      serviceId: selectedCascade?.serviceId,
    },
    {
      enabled: !!selectedCascade,
    },
  );

  const invoices = invoiceData?.items || [];
  const invoicePaginationMeta = invoiceData?.meta;

  const checkFeeInvoiceAvailability = async (
    cascadeId: string,
    serviceId: string,
  ) => {
    if (
      feeInvoiceAvailability[cascadeId] !== undefined ||
      feeInvoiceInFlightIds.current.has(cascadeId)
    ) {
      return;
    }

    feeInvoiceInFlightIds.current.add(cascadeId);

    try {
      const invoiceResponse = await fetchInvoices({
        cascadeId,
        invoiceType: InvoiceTypeEnum.Receive,
        serviceId,
      });

      const feeInvoice = invoiceResponse.items?.[0] || null;

      setFeeInvoiceAvailability((prev) => ({
        ...prev,
        [cascadeId]: feeInvoice?.hasDisbursementStatementPdf || false,
      }));
    } catch (error) {
      setFeeInvoiceAvailability((prev) => ({
        ...prev,
        [cascadeId]: false,
      }));
    } finally {
      feeInvoiceInFlightIds.current.delete(cascadeId);
    }
  };

  const handlePageRequest = useCallback(
    (nextPage: number, nextSortBy: string = CASCADES_DEFAULT_PARAMS.sortBy) => {
      setPage(nextPage);
      setSortBy(nextSortBy);
    },
    [],
  );

  useEffect(() => {
    // Skip running during placeholder data (old page still showing). Results
    // are keyed by cascade id, so a run that finishes after the page or sort
    // changed still writes a correct entry and is not dropped.
    if (isPlaceholderData || !cascades?.length) return;

    // Run checks in parallel rather than one `await` per cascade in
    // sequence — a full page was up to 50 serial `fetchInvoices` calls.
    Promise.all(
      cascades.map((cascade) =>
        checkFeeInvoiceAvailability(cascade._id, cascade.serviceId),
      ),
    ).catch((error) => {
      // checkFeeInvoiceAvailability catches its own fetch errors, so this
      // only fires for something unexpected — report it rather than drop it.
      Sentry.captureException(error);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps -- checkFeeInvoiceAvailability closes over feeInvoiceAvailability by design
  }, [cascades, isPlaceholderData]);

  const handleDownloadPdfStatement = async (statementId: string) => {
    setDownloadStatementProps({ isLoading: true, statementId });
    const fileName = CascadeFileNameEnum.StatusReportPdf;

    try {
      const activityStatementBlob = await downloadApi.activityStatement(
        fileName,
        statementId,
      );

      const file = `Activity-Statement-${statementId}-${fileName}`;

      downloadApi.downloadFile(activityStatementBlob, file);
    } catch (error) {
      // Do nothing because ErrorModal is already handed globally
    } finally {
      setDownloadStatementProps({ isLoading: false, statementId });
    }
  };

  const handleDownloadFeeInvoice = async (
    cascadeId: string,
    serviceId: string,
  ) => {
    setDownloadStatementProps({ isLoading: true, statementId: cascadeId });

    try {
      // Get the fee invoice for this cascade using direct API call
      const invoiceResponse = await fetchInvoices({
        cascadeId,
        invoiceType: InvoiceTypeEnum.Receive,
        serviceId,
      });

      const feeInvoice = invoiceResponse.items?.[0] || null;

      if (feeInvoice) {
        const documentBlob = await downloadApi.paymentStatement(
          InvoiceFileNameEnum.InvoicePdf,
          feeInvoice._id,
        );

        const file = `Fee-Statement-${feeInvoice._id}-${InvoiceFileNameEnum.InvoicePdf}`;

        downloadApi.downloadFile(documentBlob, file);
      }
    } catch (error) {
      // Do nothing because ErrorModal is already handled globally
    } finally {
      setDownloadStatementProps({ isLoading: false, statementId: cascadeId });
    }
  };

  const handleDownloadInvoicePDF = async (invoiceId: string) => {
    setDownloadInvoiceProps({ invoiceId, isLoading: true });

    const fileName = InvoiceFileNameEnum.DisbursementStatementPdf;

    try {
      const invoiceBlob = await downloadApi.paymentStatement(
        fileName,
        invoiceId,
      );

      const file = `DisbursementStatement-id-${invoiceId}-${fileName}`;

      downloadApi.downloadFile(invoiceBlob, file);
    } catch (error) {
      // Do nothing because ErrorModal is already handed globally
    } finally {
      setDownloadInvoiceProps({ invoiceId, isLoading: false });
    }
  };

  const InvoiceTableElement = () => {
    return (
      <div className="px-2 pb-3 md:px-4">
        <AppTableV2
          key="invoices"
          customEmptyTable={{
            description: 'No invoices found for this payout.',
            title: '',
          }}
          customPageCountElement={<></>}
          hasBottomNavigation={false}
          headerRowElement={
            <tr>
              <th>Beneficiary Name</th>
              <th>
                <div className="text-right">Download</div>
              </th>
            </tr>
          }
          isLoading={isInvoiceLoading}
          paginationKey="invoicesTablePage"
          paginationMeta={invoicePaginationMeta}
        >
          {invoices?.map((invoice: Invoice) => {
            return (
              <tr key={invoice._id}>
                <td>{invoice.beneficiaryWorkspaceName}</td>
                <td>
                  <div className="text-right text-base font-normal">
                    <Button
                      className="h-8 w-8"
                      icon={<Icon icon={DownloadIcon} weight="fill" />}
                      loading={
                        downloadInvoiceProps?.invoiceId === invoice._id &&
                        downloadInvoiceProps?.isLoading
                      }
                      size="icon"
                      onClick={() => handleDownloadInvoicePDF(invoice._id)}
                    >
                      <span className="sr-only">Download</span>
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </AppTableV2>
      </div>
    );
  };

  const toggleInvoiceRowView = (cascadeId: string) => {
    setSelectedCascadeId((prev) => (prev === cascadeId ? null : cascadeId));
  };

  // Define columns for AppDataTable
  const columns: ColumnDef<CascadeRowData>[] = [
    {
      accessorKey: 'cascadeDate',
      cell: ({ row }) => (
        <DateFormatter dateString={row.original.cascadeDate} />
      ),
      header: 'Date',
      meta: { sortKey: 'cascadeDate' },
    },
    {
      accessorKey: 'collectionPeriodStartDate',
      cell: ({ row }) => (
        <>
          <DateFormatter dateString={row.original.collectionPeriodStartDate} />{' '}
          - <DateFormatter dateString={row.original.collectionPeriodEndDate} />
        </>
      ),
      header: 'Collection Period',
    },
    {
      accessorKey: 'disbursementAmount',
      cell: ({ row }) => (
        <CurrencyFormatter amount={row.original.disbursementAmount} />
      ),
      header: 'Disbursed',
    },
    {
      accessorKey: 'currency',
      cell: ({ row }) => row.original.currency,
      header: 'Currency',
    },
    {
      accessorKey: 'status',
      cell: ({ row }) => (
        <Badge variant="secondary">{toTitleCase(row.original.status)}</Badge>
      ),
      header: 'Status',
    },
    {
      cell: ({ row }) => {
        const cascadeId = row.original._id;
        const serviceId = row.original.serviceId;

        return (
          <IfFeatureEnabled
            name="workspace.titles.collectionAccountManagement.downloadIndividualBeneficiary"
            isEnabled="true"
          >
            <div className="flex flex-row items-center gap-5">
              <OptionsDropdown>
                <DropdownMenuItem
                  onClick={() => handleDownloadPdfStatement(cascadeId)}
                >
                  {pdfIcon} Download Activity Statement
                </DropdownMenuItem>
                <IfFeatureEnabled name="workspace.titles.collectionAccountManagement.downloadFeeStatement">
                  <DropdownMenuItem
                    disabled={!feeInvoiceAvailability[cascadeId]}
                    onClick={() =>
                      handleDownloadFeeInvoice(cascadeId, serviceId)
                    }
                  >
                    {pdfIcon} Download Fee Statement
                  </DropdownMenuItem>
                </IfFeatureEnabled>
              </OptionsDropdown>
              <IfHasServiceAccess
                accessRequired={ServiceAccessEnum.ViewAllServiceDisbursements}
              >
                <Button
                  icon={
                    <Icon
                      icon={
                        selectedCascadeId === row.original._id
                          ? 'chevronUp'
                          : 'chevronDown'
                      }
                    />
                  }
                  size="icon"
                  variant="ghost"
                  onClick={() => toggleInvoiceRowView(row.original._id)}
                >
                  <span className="sr-only">Expand</span>
                </Button>
              </IfHasServiceAccess>
            </div>
          </IfFeatureEnabled>
        );
      },
      enableSorting: false,
      header: 'Action',
      id: 'actions',
    },
  ];

  // Column for feature-disabled scenario
  const columnsWithoutDropdown: ColumnDef<CascadeRowData>[] = [
    columns[0],
    columns[1],
    columns[2],
    columns[3],
    columns[4],
    {
      cell: ({ row }) => {
        const cascadeId = row.original._id;

        return (
          <IfFeatureEnabled
            name="workspace.titles.collectionAccountManagement.downloadIndividualBeneficiary"
            isEnabled="false"
          >
            <div className="text-center">
              <Button
                className="h-8 w-8"
                icon={<Icon icon={DownloadIcon} weight="fill" />}
                loading={
                  downloadStatementProps?.statementId === cascadeId &&
                  downloadStatementProps?.isLoading
                }
                size="icon"
                onClick={() => handleDownloadPdfStatement(cascadeId)}
              >
                <span className="sr-only">Download</span>
              </Button>
            </div>
          </IfFeatureEnabled>
        );
      },
      enableSorting: false,
      header: 'Action',
      id: 'actions',
    },
  ];

  const cascadesWithMetadata: CascadeRowData[] = cascades ?? [];

  return (
    <AppDataTable
      columns={
        isDownloadIndividualBeneficiaryEnabled
          ? columns
          : columnsWithoutDropdown
      }
      data={cascadesWithMetadata}
      emptyState={{
        description:
          'There are currently no activity statements associated with this title.',
        title: 'No Statements Available',
      }}
      isFetchingPage={isPlaceholderData}
      isLoading={isCascadesLoading}
      isRowExpanded={(cascade) => selectedCascadeId === cascade._id}
      paginationKey="cascadesTablePage"
      paginationMeta={paginationMeta}
      renderExpandedRow={() => (
        <IfHasServiceAccess
          accessRequired={ServiceAccessEnum.ViewAllServiceDisbursements}
        >
          <div className="rounded-md border border-border">
            <InvoiceTableElement />
          </div>
        </IfHasServiceAccess>
      )}
      sortBy={sortBy}
      sortParam="cascadesTableSortBy"
      onPageRequest={handlePageRequest}
    />
  );
};
